import { CAPACITY_MAX_DAYS } from "core/consts";
import { getReasonTranslation, isHospitalSearch } from "core/model/auctions";
import { addDaysUnixUtc } from "core/model/utils/dates";
import {
  AuctionRequest,
  Careprovider,
  GetOntologyType,
  NoCapacityReason,
  SearchType,
} from "core/types";
import {
  fromUnixTime,
  getHours,
  getMinutes,
  getUnixTime,
  setHours,
  setMinutes,
  startOfDay,
} from "date-fns";
import { zonedTimeToUtc } from "date-fns-tz";
import { ValidationFunction, valueDef } from "react-forms-state";
import Translations from "translations/types";

export function getNoCapacityReasonsMessage({
  getOntology,
  reasons = [],
  translations,
}: {
  getOntology: GetOntologyType;
  reasons: Array<NoCapacityReason> | undefined;
  translations: Translations;
}): string {
  return Array.isArray(reasons)
    ? reasons
        .map(
          (reason) =>
            Array.isArray(reason.values) &&
            reason.values
              .map((reasonValue: number) =>
                getReasonTranslation({
                  getOntology,
                  reason: reason.type,
                  translations,
                  value: reasonValue,
                }),
              )
              .join(", "),
        )
        .join(", ")
    : "";
}

export function shouldHandleTime({
  searchType,
}: {
  searchType: SearchType | undefined;
}): boolean {
  return isHospitalSearch(searchType);
}

export function appendTimeToCapacityDate({
  capacityDate,
  capacityTime,
}: {
  capacityDate: number;
  capacityTime: Date;
}): number {
  const start = startOfDay(fromUnixTime(capacityDate));
  return getUnixTime(
    setHours(
      setMinutes(start, getMinutes(capacityTime)),
      getHours(capacityTime),
    ),
  );
}

export function setTimeToMidnight(capacityDate: number) {
  return getUnixTime(
    zonedTimeToUtc(startOfDay(fromUnixTime(capacityDate)), "UTC"),
  );
}

function validateSingleDate({
  dateKey,
  dateToValidate,
  required,
  translations,
}: {
  dateKey: string;
  dateToValidate: number | undefined;
  required?: boolean;
  translations: Translations;
}) {
  const timestampCurrent = addDaysUnixUtc(0, { endOfDay: true });
  const timestampCapacityMax = addDaysUnixUtc(CAPACITY_MAX_DAYS, {
    endOfDay: true,
  });
  const errorMessageDate =
    translations.careproviderApp.settings.capacity.tileNoCapacity
      .errorDateInPast;

  if (dateToValidate) {
    if (
      dateToValidate > timestampCapacityMax ||
      dateToValidate < timestampCurrent
    ) {
      return {
        [dateKey]: {
          customMessage: errorMessageDate,
        },
      };
    }
  }

  if (required && !dateToValidate) {
    return {
      [dateKey]: {
        customMessage: errorMessageDate,
      },
    };
  }

  return true;
}

export const validateCapacity: ValidationFunction = ({
  dateKey,
  props,
  required,
  value,
}: {
  dateKey: string;
  props: {
    auctionRequest?: AuctionRequest;
    formInputValue: Careprovider;
    translations: Translations;
  };
  required?: boolean;
  value: {
    available_date?: number;
    capacity_date?: number;
    has_capacity?: boolean;
  };
}) => {
  const { translations } = props;
  const { available_date, capacity_date, has_capacity } = value;
  const dateToValidate = capacity_date || available_date;

  if (has_capacity) return true;

  return validateSingleDate({
    dateKey,
    dateToValidate,
    required,
    translations,
  });
};

export function getCapacityModel(location: "request" | "settings") {
  const withSettings = location === "settings";

  return {
    ...valueDef("has_capacity"),
    ...valueDef(withSettings ? "available_date" : "capacity_date", {
      convertOut: (
        value: number,
        props: { formInputValue: Careprovider },
        modelValues: Map<string, boolean | Date>,
      ) => {
        const has_capacity = modelValues.get("has_capacity") as boolean;

        // if has_capacity is set to true, remove the available_date
        if (has_capacity) return null;

        // if no capacity_time set and has_capacity is false, we set the available_date to midnight
        return setTimeToMidnight(value);
      },
    }),
  };
}
